import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useAppState } from "../../hooks/useAppState";
import { getVanillaEl } from "../../domBridge";
import { appState } from "../../../state";
import { notifyStateChange } from "../../../stateBus";
import { sortMediaInPlace } from "../../../sortLogic";
import type { SortDir, SortKey } from "../../../types";

const SORT_KEYS: SortKey[] = ["name", "date", "type", "size"];

function labelForSortKey(k: SortKey): string {
  if (k === "name") return "Name";
  if (k === "date") return "Date";
  if (k === "type") return "Type";
  return "Size";
}

// appState.sortState is an object mutated in place by the original vanilla
// code - same useSyncExternalStore reference-identity gotcha as the Sets
// (see utils.ts's setWith/setWithout comment) - always reassign a new
// object here, never mutate .key/.dir directly.
function setSortDir(dir: SortDir): void {
  appState.sortState = { ...appState.sortState, dir };
  sortMediaInPlace();
  notifyStateChange();
}

function setSortKey(key: SortKey): void {
  let dir = appState.sortState.dir;
  if (key === "date" && dir !== "desc") dir = "desc";
  appState.sortState = { key, dir };
  sortMediaInPlace();
  notifyStateChange();
}

export function SortToolbar() {
  const sortState = useAppState((s) => s.sortState);
  const [menuOpen, setMenuOpen] = useState(false);
  const anchor = getVanillaEl<HTMLDivElement>("sortToolbarAnchor");

  useEffect(() => {
    if (!menuOpen) return;
    function onDocClick(e: MouseEvent) {
      if (anchor && !anchor.contains(e.target as Node)) setMenuOpen(false);
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") setMenuOpen(false);
    }
    document.addEventListener("click", onDocClick);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("click", onDocClick);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [menuOpen, anchor]);

  if (!anchor) return null;

  return createPortal(
    <div className="sort-toolbar-wrap">
      <div className="sort-pill">
        <button
          className="sort-pill-btn"
          type="button"
          aria-haspopup="true"
          aria-expanded={menuOpen}
          onClick={(e) => {
            e.stopPropagation();
            setMenuOpen((o) => !o);
          }}
        >
          <span className="sort-label">{labelForSortKey(sortState.key)}</span>
          <i className="bi bi-chevron-down" />
        </button>
        <button
          className="sort-dir-btn"
          type="button"
          aria-label="Toggle sort direction"
          onClick={() => setSortDir(sortState.dir === "asc" ? "desc" : "asc")}
        >
          <i className={`bi ${sortState.dir === "asc" ? "bi-sort-up" : "bi-sort-down"}`} />
        </button>
      </div>

      <div className={`sort-menu${menuOpen ? "" : " d-none"}`} role="menu">
        {SORT_KEYS.map((k) => (
          <button
            key={k}
            className={`sort-item${sortState.key === k ? " active" : ""}`}
            type="button"
            onClick={() => {
              setSortKey(k);
              setMenuOpen(false);
            }}
          >
            {labelForSortKey(k)}
          </button>
        ))}
      </div>
    </div>,
    anchor,
  );
}
